import {
  CreateBrlTradeRequest,
  CreateBrlTradeResponse,
} from "@services/models/Prime/CreateBrlTradeModel";
import {
  GetCadInstancesRequest,
  GetCadInstancesResponse,
} from "@services/models/Prime/GetCadInstancesModel";
import {
  GetIvyInstancesRequest,
  GetIvyInstancesResponse,
} from "@services/models/Prime/GetIvyInstancesModel";
import {
  GetPHPInstancesRequest,
  GetPHPInstancesResponse,
} from "@services/models/Prime/GetPHPInstancesModel";
import {
  GetPixTransactionRequest,
  PixTransactionData,
} from "@services/models/Prime/GetPixTransactionModel";
import {
  GetPrimeCadTradeDetailsRequest,
  GetPrimeCadTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeCadTradeDetailsModel";
import {
  GetPrimeEuropeTradeDetailsRequest,
  GetPrimeEuropeTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeEuropeTradeDetailsModel";
import {
  GetPrimePHPTradeDetailsRequest,
  GetPrimePHPTradeDetailsResponse,
} from "@services/models/Prime/GetPrimePHPTradeDetailsModel";
import {
  GetPrimeTradeDetailsRequest,
  GetPrimeTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeTradeDetailsModel";
import {
  GetPrimeTradesListRequest,
  GetPrimeTradesListResponse,
} from "@services/models/Prime/GetPrimeTradeListModel";
import {
  GetReportsDataReponse,
  GetReportsDataRequest,
} from "@services/models/Prime/GetReportsDataModel";
import {
  GetTradeCallBackLogsRequest,
  GetTradeCallBackLogsResponse,
} from "@services/models/Prime/GetTradeCallBackLogsModel";
import { GetNtxBalanceResponse } from "@services/models/Prime/NtxBalanceModel";
import { AxiosInstance } from "axios";
import { createApiClient } from "../api/createApiClient";

class PrimeApiService {
  private api: AxiosInstance;
  private silentApi: AxiosInstance;
  constructor() {
    this.api = createApiClient();
    this.silentApi = createApiClient({ notifyErrors: false });
  }

  public async getPrimeTradesList(
    data: GetPrimeTradesListRequest,
  ): Promise<GetPrimeTradesListResponse> {
    try {
      const response = await this.api.get(
        `/analyticsDashboard/getPrimeTradesList`,
        {
          params: {
            tradeType: data.tradeType,
            tradeStatus: data.tradeStatus,
            page: data.page,
            rows: data.rows,
            primeType: data.primeType ? data.primeType : undefined,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async searchPrimeTrade(
    searchTerm: string,
    primeType?: "IN" | "BR",
  ): Promise<GetPrimeTradesListResponse> {
    try {
      const response = await this.api.get(
        `/analyticsDashboard/searchPrimeTrade`,
        {
          params: {
            searchTerm: searchTerm.trim(),
            primeType: primeType,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPrimeTradeDetails(
    data: GetPrimeTradeDetailsRequest,
  ): Promise<GetPrimeTradeDetailsResponse> {
    try {
      const response = await this.api.get(
        `/analyticsDashboard/getPrimeTradeDetails`,
        {
          params: {
            tradeId: data.tradeId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getIvyInstancesList(
    data: GetIvyInstancesRequest,
  ): Promise<GetIvyInstancesResponse> {
    try {
      const response = await this.api.get(
        "/analyticsDashboard/getIvyInstancesList",
        {
          params: {
            type: data.type,
            page: data.page,
            rows: data.rows,
            searchTerm: data.searchTerm?.trim() || undefined,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getIvyInstanceDetails(
    data: GetPrimeEuropeTradeDetailsRequest,
  ): Promise<GetPrimeEuropeTradeDetailsResponse> {
    try {
      const response = await this.api.get(
        `/analyticsDashboard/getIvyInstanceDetails`,
        {
          params: {
            instanceId: data.instanceId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getTradeCallbackLogs(
    data: GetTradeCallBackLogsRequest,
    endPoint?: string,
  ): Promise<GetTradeCallBackLogsResponse> {
    try {
      const response = await this.api.get(
        `${endPoint ? endPoint : "/analyticsDashboard/getTradeCallbackLogs"}`,
        {
          params: {
            tradeId: data.tradeId,
            instanceId: data.instanceId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getReportsData(
    data: GetReportsDataRequest,
  ): Promise<GetReportsDataReponse> {
    try {
      const response = await this.api.get(
        `/analyticsDashboard/getReportsData`,
        {
          params: {
            startDate: data.startDate,
            endDate: data.endDate,
            merchantId: data.merchantId,
            isCompleted: data.isCompleted,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /** Cross ramp report for EUR Payin / EUR Payout (Ivy) – same response shape as getReportsData */
  public async getIvyReportsData(
    data: GetReportsDataRequest,
  ): Promise<GetReportsDataReponse> {
    try {
      const response = await this.api.get(
        `/analyticsDashboard/getIvyReportsData`,
        {
          params: {
            startDate: data.startDate,
            endDate: data.endDate,
            merchantId: data.merchantId,
            isCompleted: data.isCompleted,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async createBrlTrade(
    data: CreateBrlTradeRequest,
  ): Promise<CreateBrlTradeResponse> {
    try {
      const response = await this.api.post<CreateBrlTradeResponse>(
        `/analyticsDashboard/prime/BR/PIX/instance`,
        {
          isBuyTrade: 1,
          merchantOrderId: data.merchantOrderId,
          amount: data.amount,
          userDetails: {},
          currencySymbol: data.currencySymbol,
          callBackUrl: data.callBackUrl,
          redirectUrl: data.redirectUrl,
          userEmail: data.userEmail,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPixTransaction(
    data: GetPixTransactionRequest,
  ): Promise<PixTransactionData> {
    try {
      const response = await this.api.get<PixTransactionData>(
        `/analyticsDashboard/ntxPixTransaction/${data.id}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPHPInstancesList(
    data: GetPHPInstancesRequest,
  ): Promise<GetPHPInstancesResponse> {
    try {
      const response = await this.api.get(
        "/analyticsDashboard/getPHPInstancesList",
        {
          params: {
            type: data.type,
            page: data.page,
            rows: data.rows,
            searchTerm: data.searchTerm?.trim() || undefined,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPHPInstanceDetails(
    data: GetPrimePHPTradeDetailsRequest,
  ): Promise<GetPrimePHPTradeDetailsResponse> {
    try {
      const response = await this.api.get(
        `/analyticsDashboard/getPHPInstanceDetails`,
        {
          params: {
            instanceId: data.instanceId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCadInstancesList(
    data: GetCadInstancesRequest,
  ): Promise<GetCadInstancesResponse> {
    try {
      const response = await this.api.get(
        "/analyticsDashboard/getCadInstancesList",
        {
          params: {
            type: data.type,
            page: data.page,
            rows: data.rows,
            searchTerm: data.searchTerm?.trim() || undefined,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCadInstanceDetails(
    data: GetPrimeCadTradeDetailsRequest,
  ): Promise<GetPrimeCadTradeDetailsResponse> {
    try {
      const response = await this.api.get(
        `/analyticsDashboard/getCadInstanceDetails`,
        {
          params: {
            instanceId: data.instanceId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getNtxBalance(options?: {
    silent?: boolean;
  }): Promise<GetNtxBalanceResponse> {
    const client = options?.silent ? this.silentApi : this.api;
    const response = await client.get<GetNtxBalanceResponse>(
      "/analyticsDashboard/getNtxBalance",
    );
    return response.data;
  }
}

export const primeServices = new PrimeApiService();
